import { useContext, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Grid, Spacer } from '@zillow/constellation';
import { LoadingButton } from '@zg-rentals/loading-components';
import { useClientAppConfigWebHandler_getFeature } from '@zg-rentals/rental-manager-env';

import { FreemiumContext } from '../Freemium/context';
import { RMWContext } from '../../providers/RMWContext';
import { LoadingMapContext } from '../../providers/LoadingMapContext/context';

import { useSelector } from '../../selectors/useSelector';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../constants/FreemiumConstants';
import { VERTICAL_DISPLAY_TYPE } from '../../constants/PropertiesConstants';
import { TENANT_PLACEMENT_COOKIES } from '../../constants/CookieConstants';
import { screenSize as SCREEN_SIZE } from '../../utils/browserUtils';

import accessUtils from '../../utils/accessUtils';

import ConnectedModuleCard from '../../components/ConnectedModuleCard';
import ListingAvailability from '../ConnectedListing/components/ListingAvailability';
import PremiumCTA from '../ConnectedListing/components/PremiumCTA';

import Details from '../ConnectedListing/components/Details';
import TenantPlacementUpsellModal from '../Modal/TenantPlacementUpsellModal';

import ConnectedNetworkView, { DISPLAY_TYPE } from '../ConnectedNetworkView';
import TenantPlacementUpsellBanner from '../TenantPlacementUpsellBanner';

import { Text } from '@zillow/constellation-10';
import { singleTextLineStyles } from '../../styles';

import { ListingModuleDetailsCta } from './components/ListingModuleDetailsCta';
import { ListingModuleHeading } from './components/ListingModuleHeading';
import WhenListingExpireLearnMore from './components/WhenListingExpireLearnMore';

import type { MouseEvent } from 'react';

const ListingModule = () => {
  const [showTenantPlacementModal, setShowTenantPlacementModal] = useState(false);

  const {
    freemiumState: { canAccessTopOfFunnelPremium },
  } = useContext(FreemiumContext);

  const { paths } = useContext(RMWContext);

  const {
    loadingByComponent: { ListingModule: isListingModuleLoading },
  } = useContext(LoadingMapContext);

  const {
    cookieEnabledServices,
    user,
    currentProperty,
    active,
    activeProductType,
    expirationTimestamp: activeProductExpirationTimestamp,
    constellationScreenSize,
    expirationDateTimestamp,
    isFreemiumEnabled,
    listingAlias,
    rentZestimate,
    shouldCheckIn,
    status,
    syndication,
  } = useSelector((state) => {
    return {
      cookieEnabledServices: state.app.cookieEnabledServices,
      user: state.user,
      currentProperty: state.properties.currentProperty,
      active: state.properties.currentProperty.active,
      activeProductType: state.properties.currentProperty.freemium.activeProduct.type,
      expirationTimestamp: state.properties.currentProperty.freemium.activeProduct.expirationTimestamp,
      constellationScreenSize: state.app.constellationScreenSize,
      expirationDateTimestamp: state.properties.currentProperty.expirationDateTimestamp,
      isFreemiumEnabled: state.properties.currentProperty.freemium.isEnabled,
      listingAlias: state.properties.currentProperty.listingAlias,
      rentZestimate: state.properties.currentProperty.rentZestimate,
      shouldCheckIn: state.properties.propertyOverview.listingAvailability.shouldCheckIn,
      status: state?.properties?.currentProperty?.syndication?.overview?.status,
      syndication: state.properties.currentProperty.syndication,
    };
  }, shallowEqual);

  const { data: enabledStates = '' } = useClientAppConfigWebHandler_getFeature({
    appName: 'zrm_web',
    featureName: 'tenantPlacementStates',
  });

  const canAccessModularListing = accessUtils.canAccessModularListing({
    currentProperty,
    cookieEnabledServices,
    user,
  });

  const canAccessTenantPlacementDashboardModal = accessUtils.canAccessTenantPlacement({
    enabledStates,
    currentProperty,
    user,
    cookie: TENANT_PLACEMENT_COOKIES.HAS_CLICKED_DASHBOARD_POST,
  });

  const canAccessTenantPlacementListingModuleBanner = accessUtils.canAccessTenantPlacementListingModuleBanner({
    currentProperty,
    user,
    enabledStates,
  });

  const listingPath = `${paths.propertiesPage}/${listingAlias}/${
    canAccessModularListing ? 'create-listing' : 'listing'
  }`;
  const shouldDisplayListingAvailability = Boolean(expirationDateTimestamp && shouldCheckIn);
  const gridRepeat =
    constellationScreenSize === SCREEN_SIZE.XS || constellationScreenSize === SCREEN_SIZE.SM ? '1' : 'auto-fit';

  const isActiveProductTypePremium = activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM;
  const isActiveProductTypeFree = activeProductType === PRODUCT_TYPE_NAME_TO_ID.FREE;

  // keep same logic as in ConnectedListing
  const displayPremiumCTA = (isFreemiumEnabled && active) || (canAccessTopOfFunnelPremium && !isActiveProductTypeFree);

  const activeCTA = 'Edit your listing';
  const inactiveCTA = 'List your property';

  const handleDetailsClick = (event: MouseEvent<HTMLAnchorElement>) => {
    handleGoToListingClick(event);
  };

  const handleGoToListingClick = (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    event.preventDefault();
    if (canAccessTenantPlacementDashboardModal && !currentProperty.dateLastActivatedTimestamp && !active) {
      setShowTenantPlacementModal(true);
    } else {
      transitionToListingPath();
    }
  };

  const transitionToListingPath = () => {
    window.router.transitionTo(listingPath, '_self');
  };

  const getListingTypeModuleCardHeading = () => {
    if (isFreemiumEnabled && isActiveProductTypePremium) {
      return 'Premium listing';
    }

    if (isFreemiumEnabled && active && isActiveProductTypeFree) {
      return 'Basic listing';
    }

    return 'Listing';
  };

  return (
    <ConnectedModuleCard
      data-testid="ListingModule"
      leftComponent={
        <ListingModuleHeading
          isPremiumListing={isFreemiumEnabled && isActiveProductTypePremium}
          headingText={getListingTypeModuleCardHeading()}
        />
      }
      ctaComponent={<ListingModuleDetailsCta listingPath={listingPath} onClick={handleDetailsClick} />}
      ctaFontColor="text.subtle"
      ctaFontType="body-sm"
      justifyHeadingContent={'space-between'}
      isLoading={false}
      includeBorderTop={isFreemiumEnabled && isActiveProductTypePremium}
    >
      {shouldDisplayListingAvailability && (
        <Spacer marginBottom={{ default: 'lg' }}>
          <ListingAvailability />
        </Spacer>
      )}

      <Details displayType={VERTICAL_DISPLAY_TYPE.SIMPLE} rentZestimate={rentZestimate} />

      {Boolean(active && !shouldDisplayListingAvailability) && <WhenListingExpireLearnMore />}

      {displayPremiumCTA && isActiveProductTypePremium && (
        <PremiumCTA
          isActiveProductTypeFree={isActiveProductTypeFree}
          activeProductExpirationTimestamp={activeProductExpirationTimestamp}
          isFromListingModule={true}
        />
      )}

      <Spacer marginBottom={{ default: 'sm', lg: 'md' }} />

      {active ? (
        <Grid display="grid" gridTemplateColumns={`repeat(${gridRepeat}, minmax(0, 1fr))`} gap={{ default: 'xs' }}>
          <Grid>
            <ConnectedNetworkView
              displayType={DISPLAY_TYPE.DROPDOWN_BUTTON}
              label="View your listing"
              size="md"
              status={status}
              syndication={syndication}
              isLoading={isListingModuleLoading}
            />
          </Grid>

          <Grid>
            <LoadingButton
              tone="neutral"
              emphasis="outlined"
              fluid={true}
              onClick={handleGoToListingClick}
              title={activeCTA}
              isLoading={isListingModuleLoading}
              loadingWidth="100%"
            >
              <Text textStyle="body-bold" css={singleTextLineStyles}>
                {activeCTA}
              </Text>
            </LoadingButton>
          </Grid>
        </Grid>
      ) : (
        <LoadingButton
          fluid={true}
          tone="neutral"
          emphasis="outlined"
          onClick={handleGoToListingClick}
          title={inactiveCTA}
          isLoading={isListingModuleLoading}
          loadingWidth="100%"
        >
          <Text textStyle="body-bold" css={singleTextLineStyles}>
            {inactiveCTA}
          </Text>
        </LoadingButton>
      )}

      {canAccessTenantPlacementListingModuleBanner && (
        <Spacer marginTop={{ default: 'sm', lg: 'md' }}>
          <TenantPlacementUpsellBanner />
        </Spacer>
      )}

      {showTenantPlacementModal && (
        <TenantPlacementUpsellModal
          onClose={() => setShowTenantPlacementModal(false)}
          onPostListingClick={transitionToListingPath}
        />
      )}
    </ConnectedModuleCard>
  );
};

export default ListingModule;
